// Meta click IDs. An ad click lands with ?fbclid=<click id>, which Meta's pixel
// and Parameter Builder (src/proxy.ts) store in the _fbc cookie as
// fb.<subdomain index>.<creation time in ms>.<click id>. Parameter Builder may
// append one more segment, its appendix, which is not part of the click ID.
// A click expires 90 days after it was first seen, and that time is never
// refreshed. See specs/meta-click-id.md.
//
// Nothing here throws, whatever the input: a bad cookie or URL must never break
// a page, login, checkout or an event.

export const META_FBC_COOKIE = "_fbc";
export const META_CLICK_ID_PARAM = "fbclid";
/** Meta's pixel keeps a click while it is younger than this. */
export const META_FBC_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
/** The pixel stamps clicks with the visitor's clock, which can run fast. */
export const META_FBC_FUTURE_ALLOWANCE_MS = 5 * 60 * 1000;

// URL-safe, and short enough to keep a whole _fbc within Stripe's
// 500-character metadata limit.
const CLICK_ID_PATTERN = /^[A-Za-z0-9_~-]{1,400}$/;
const FBC_PATTERN = /^fb\.\d{1,2}\.(\d{13})\.([^.]+)(?:\.([^.]+))?$/;
// Parameter Builder's 2-character language tokens (capi-param-builder
// Constants.js). Its 8-character appendix is 6 base64url bytes: format 0x01,
// a language index of 1-6, then the appendix type and SDK version.
const LANGUAGE_TOKENS = ["AQ", "Ag", "Aw", "BA", "BQ", "Bg"];
const APPENDIX_PATTERN = /^[A-Za-z0-9_-]{8}$/;

export interface MetaFbc {
  /** The cookie value as stored, appendix included. */
  value: string;
  createdAtMs: number;
  /** Segment 3, without Parameter Builder's appendix. */
  clickId: string;
  /** Whether the click ID and appendix are in a format Meta is known to write. */
  isRecognised: boolean;
}

/** A click ID as it may appear in ?fbclid=, or null. */
export function parseMetaClickId(value: unknown): string | null {
  return typeof value === "string" && CLICK_ID_PATTERN.test(value)
    ? value
    : null;
}

/** The parts of an _fbc value, or null when it doesn't have the _fbc shape. */
export function parseMetaFbc(value: unknown): MetaFbc | null {
  if (typeof value !== "string") {
    return null;
  }
  const match = FBC_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const [, createdAt, clickId, appendix] = match;
  return {
    value,
    createdAtMs: Number(createdAt),
    clickId,
    isRecognised:
      CLICK_ID_PATTERN.test(clickId) &&
      (appendix === undefined || isParamBuilderAppendix(appendix)),
  };
}

/** Recognised, younger than 90 days and not from the future at nowMs. */
export function isMetaFbcUsable(fbc: MetaFbc, nowMs: number): boolean {
  return (
    fbc.isRecognised &&
    nowMs - fbc.createdAtMs < META_FBC_MAX_AGE_MS &&
    fbc.createdAtMs - nowMs <= META_FBC_FUTURE_ALLOWANCE_MS
  );
}

/** The _fbc value, unchanged, when it is usable at nowMs; else undefined. */
export function getUsableMetaFbc(
  value: unknown,
  nowMs: number,
): string | undefined {
  const fbc = parseMetaFbc(value);
  return fbc && isMetaFbcUsable(fbc, nowMs) ? fbc.value : undefined;
}

/** Seconds until the click expires: the most a cookie holding it may live. */
export function getMetaFbcRemainingSeconds(fbc: MetaFbc, nowMs: number): number {
  return Math.floor((fbc.createdAtMs + META_FBC_MAX_AGE_MS - nowMs) / 1000);
}

function isParamBuilderAppendix(appendix: string): boolean {
  if (LANGUAGE_TOKENS.includes(appendix)) {
    return true;
  }
  if (!APPENDIX_PATTERN.test(appendix)) {
    return false;
  }
  const bytes = atob(appendix.replace(/-/g, "+").replace(/_/g, "/"));
  const language = bytes.charCodeAt(1);
  return bytes.charCodeAt(0) === 0x01 && language >= 1 && language <= 6;
}
